import React, { useState } from 'react';
import { ToggleButtonGroup, ToggleButton, Box, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { MultipleChoiceOption } from 'controllers/types';

const StyledToggleButton = styled(ToggleButton)(({ theme }) => ({
  '&&&': {
    boxShadow: '0px 0.8px 1.5px rgba(0, 0, 0, 0.1), 0px 6px 12px rgba(0, 0, 0, 0.2)',
    margin: '10px',
    paddingLeft: '10px',
    borderRadius: '5px',
    '&.Mui-selected': {
      background: theme.palette.lessonMultipleChoiceToggleButton.selected,
    },
  },
}));

type LessonEquationMultipleChoiceProps = {
  handleMultipleChoice: (selected: string) => void;
  multipleChoiceOptions: MultipleChoiceOption[];
  prevSelected: { [key: string]: boolean } | null;
  segmentId: number;
};

export const MultipleChoice = ({
  multipleChoiceOptions,
  handleMultipleChoice,
  prevSelected,
  segmentId,
}: LessonEquationMultipleChoiceProps) => {
  const [selected, setSelected] = useState('');
  const rowLayout = useMediaQuery('(min-width:600px)');

  const handleSelected = (_event: React.MouseEvent, selectedOption: '') => {
    handleMultipleChoice(selectedOption);
    setSelected(selectedOption);
  };
  const theme = useTheme();

  // boolean value stored for each choice in parent to record what has been selected so far
  const wasPreviouslySelected = (choiceLabel: string) => prevSelected && prevSelected[`${segmentId}-${choiceLabel}`];

  const conditionalStyles = (choiceLabel: string) => {
    if (wasPreviouslySelected(choiceLabel)) {
      return {
        background: theme.palette.action.disabledBackground,
        color: theme.palette.action.disabled,
        border: 'none',
        '&:hover': { background: theme.palette.action.disabledBackground },
      };
    }
    return {
      background: theme.palette.lessonMultipleChoiceToggleButton.main,
      color: theme.palette.lessonMultipleChoiceToggleButton.contrastText,
      boxShadow: '0px 0.8px 1.5px rgba(0, 0, 0, 0.1), 0px 6px 12px rgba(0, 0, 0, 0.2)',
      '&:hover': { background: theme.palette.lessonMultipleChoiceToggleButton.hovered },
    };
  };

  return (
    <Box display='flex' justifyContent='center'>
      <ToggleButtonGroup
        value={selected}
        orientation={rowLayout ? 'horizontal' : 'vertical'}
        exclusive
        onChange={handleSelected}
      >
        {multipleChoiceOptions.map(choice => (
          <StyledToggleButton
            fullWidth
            key={choice.key}
            value={choice.label}
            aria-label={choice.label}
            sx={conditionalStyles(choice.label)}
          >
            <Box px='10px'>{choice.label}</Box>
          </StyledToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
};
